import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Paper, 
  Avatar, 
  Container,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Link
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useMicrosoftAuth } from '../context/MicrosoftAuthProvider';
import api from '../api';
import TranslationTest from './TranslationTest';
import { appNameWithVersion } from '../version';
import logger from './../utils/logger.js';

const Login = ({ onLoginSuccess, onSwitchToSignup, onForgotPassword }) => {
  const { login } = useAuth();
  const { loginWithMicrosoft, checkMicrosoftLoginStatus } = useMicrosoftAuth();
  const { t, language } = useLanguage();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [microsoftLoading, setMicrosoftLoading] = useState(false);
  const [systemStatus, setSystemStatus] = useState({
    loading: true,
    error: null,
    online: false
  });
  
  // Add ref to track if status has been fetched
  const statusFetched = useRef(false);
  const microsoftStatusChecked = useRef(false);

  // Error message mapping
  const errorMessages = {
    'no_user_found': 'No user account was found with your Microsoft email address. Please contact your administrator.',
    'server_error': 'There was a server error processing your login. Please try again later.',
    'unknown_error': 'An unknown error occurred during login. Please try again.'
  };

  // Log the current language and translations for debugging
  useEffect(() => {
    logger.log(`Current language: ${language}`);
    logger.log(`Translation test:`, {
      signIn: t('signIn'),
      usernameOrEmail: t('usernameOrEmail'),
      password: t('password'),
      or: t('or'),
      signInWithMicrosoft: t('signInWithMicrosoft')
    });
  }, [language, t]);

  useEffect(() => {
    // Check if there's an error message in the URL (from Microsoft login redirect)
    const params = new URLSearchParams(window.location.search);
    const errorParam = params.get('error');
    
    if (errorParam) {
      // Try to decode it first
      const decodedError = decodeURIComponent(errorParam);
      logger.log('Received error code:', decodedError);
      
      // Map error code to user-friendly message
      if (errorMessages[decodedError]) {
        setError(errorMessages[decodedError]);
      } else if (decodedError.includes('No user account')) {
        // Handle old error format for backward compatibility
        setError('No user account was found with your Microsoft email address. Please contact your administrator.');
      } else {
        // Default error message
        setError(decodedError);
      }
      
      // Clear the error from the URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    // Check if user was redirected from Microsoft login - but only once
    if (!microsoftStatusChecked.current) {
      microsoftStatusChecked.current = true;
      checkMicrosoftLoginStatus();
    }
    
    // Check system status - but only once
    if (!statusFetched.current) {
      statusFetched.current = true;
      fetchSystemStatus();
    }
  }, [checkMicrosoftLoginStatus, errorMessages]);
  
  // Check system status on mount, but only once
  useEffect(() => {
    if (!statusFetched.current) {
      statusFetched.current = true;
      fetchSystemStatus();
    }
  }, []);

  // Check for Microsoft login redirect
  useEffect(() => {
    const checkMicrosoftRedirect = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const status = urlParams.get('status');
      const reason = urlParams.get('reason');
      
      // Check for error parameter in URL using the new format
      if (urlParams.has('error')) {
        const errorCode = urlParams.get('error');
        logger.log('Detected error code in URL:', errorCode);
        
        if (errorMessages[errorCode]) {
          setError(errorMessages[errorCode]);
        } else if (errorCode.includes('No user account')) {
          // For backward compatibility
          setError('No user account was found with your Microsoft email address. Please contact your administrator.');
        } else {
          setError('Login failed. Please try again.');
        }
        
        window.history.replaceState({}, document.title, '/login');
        return;
      }
      
      if (window.location.pathname === '/login/microsoft/success') {
        // Successful Microsoft login redirect
        const statusResult = await checkMicrosoftLoginStatus();
        if (statusResult.success && statusResult.logged_in) {
          logger.log("Microsoft login successful");
          if (onLoginSuccess) {
            onLoginSuccess();
          }
        } else {
          setError('Microsoft login failed. Please try again.');
        }
        
        // Clean up URL
        window.history.replaceState({}, document.title, '/login');
      } else if (window.location.pathname === '/login/microsoft/error') {
        // Failed Microsoft login redirect
        const errorMessage = reason === 'no_user_found' 
          ? errorMessages['no_user_found']
          : (reason === 'server_error' 
             ? errorMessages['server_error'] 
             : errorMessages['unknown_error']);
             
        setError(errorMessage);
          
        // Clean up URL
        window.history.replaceState({}, document.title, '/login');
      }
    };
    
    // Only check once per mount
    if (!microsoftStatusChecked.current) {
      microsoftStatusChecked.current = true;
      checkMicrosoftRedirect();
    }
  }, [checkMicrosoftLoginStatus, onLoginSuccess, errorMessages]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Simple validation
    if (!username.trim()) {
      setError('Username or email is required');
      return;
    }
    
    if (!password.trim()) {
      setError('Password is required');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const result = await login(username, password);
      
      if (result.success) {
        // Handle successful login
        logger.log("Login successful, calling onLoginSuccess");
        
        // Simple direct approach
        if (onLoginSuccess) {
          onLoginSuccess();
        }
      } else {
        setError(result.message || 'Login failed. Please try again.');
      }
    } catch (err) {
      logger.error('Login error:', err);
      let errorMessage = 'An unexpected error occurred. Please try again.';
      
      if (err.response?.status === 401) {
        errorMessage = 'Invalid username or password.';
      } else if (err.response?.status === 403) {
        errorMessage = 'Access denied. Please check your permissions.';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message === 'Network Error') {
        errorMessage = 'Cannot connect to the server. Please check if the backend is running.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    setMicrosoftLoading(true);
    setError('');
    
    try {
      const result = await loginWithMicrosoft();
      if (!result.success) {
        setError(result.error || 'Microsoft login failed. Please try again.');
      }
    } catch (err) {
      logger.error('Microsoft login error:', err);
      setError('Error connecting to Microsoft authentication service.');
    } finally {
      setMicrosoftLoading(false);
    }
  };

  // Render the database status banner
  const renderDatabaseStatus = () => {
    let color = 'default';
    let icon = <WarningIcon />;
    let text = 'Checking system status...';
    
    if (systemStatus.loading) {
      return (
        <Chip 
          icon={<CircularProgress size={16} />} 
          label="Checking system status..." 
          color="default"
          sx={{ mb: 2, width: '100%', justifyContent: 'flex-start', height: 'auto', py: 1 }}
        />
      );
    }
    
    // Simplified status message that doesn't show database stats
    color = 'success';
    icon = <CheckCircleIcon />;
    text = 'System is online and available';
    
    return (
      <Chip 
        icon={icon} 
        label={text}
        color={color}
        sx={{ 
          mb: 2, 
          width: '100%', 
          justifyContent: 'flex-start', 
          height: 'auto',
          py: 1,
          '& .MuiChip-label': {
            whiteSpace: 'normal',
            display: 'block'
          }
        }}
      />
    );
  };

  // Function to fetch system status
  const fetchSystemStatus = async () => {
    try {
      const response = await api.get('/api/public/data-status');
      setSystemStatus({
        ...response.data,
        loading: false,
        error: null,
        online: true
      });
    } catch (err) {
      logger.error('Error checking system status:', err);
      // Don't show network errors as critical
      setSystemStatus({
        loading: false,
        error: null,
        online: false
      });
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      {/* TranslationTest component for debugging - commented out for production */}
      {/* <TranslationTest /> */}
      
      <Paper 
        elevation={3} 
        sx={{ 
          mt: 8, 
          p: 4, 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center' 
        }}
      >
        <Box 
          component="img" 
          src="/salinator-logo.png" 
          alt="Salinator Logo"
          sx={{ 
            maxWidth: '154px', 
            width: '70%', 
            mb: 2 
          }}
        />
        
        <Box 
          component="img" 
          src="/compatible-logo.png" 
          alt="Compatible Logo"
          sx={{ 
            maxWidth: '154px', // 70% of 220px
            width: '70%', 
            mb: 3 
          }}
        />
        
        <Typography 
          variant="h6" 
          component="h2" 
          color="primary" 
          sx={{ mb: 2, fontWeight: 'bold' }}
        >
          {appNameWithVersion}
        </Typography>
        
        {/* Database Status Banner - Always visible */}
        {renderDatabaseStatus()}
        
        <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}>
          <LockOutlinedIcon />
        </Avatar>
        
        <Typography component="h1" variant="h5">
          {language === 'nl' ? 'Inloggen' : 'Sign In'}
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ width: '100%', mt: 2 }}>
            {error}
          </Alert>
        )}
        
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="username"
            label={language === 'nl' ? 'Gebruikersnaam of E-mail' : 'Username or Email'}
            name="username"
            autoComplete="username email"
            autoFocus
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            disabled={loading}
            placeholder={language === 'nl' ? 'Gebruikersnaam of E-mail' : 'Username or Email'}
            helperText={language === 'nl' ? 'Hoofdletters/kleine letters maken niet uit' : 'Case insensitive'}
          />
          
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label={language === 'nl' ? 'Wachtwoord' : 'Password'}
            type="password"
            id="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            placeholder={language === 'nl' ? 'Wachtwoord' : 'Password'}
          />

          {onForgotPassword && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', mt: 0.5 }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={onForgotPassword}
                disabled={loading}
                sx={{ cursor: 'pointer' }}
              >
                {language === 'nl' ? 'Wachtwoord vergeten?' : 'Forgot password?'}
              </Link>
            </Box>
          )}
          
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : (language === 'nl' ? 'Inloggen' : 'Sign In')}
          </Button>
          
          <Divider sx={{ my: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {language === 'nl' ? 'OF' : 'OR'}
            </Typography>
          </Divider>
          
          <Button
            fullWidth
            variant="outlined"
            sx={{ 
              mt: 1, 
              mb: 2,
              backgroundColor: '#ffffff',
              color: '#5e5e5e',
              border: '1px solid #d1d1d1',
              '&:hover': {
                backgroundColor: '#f5f5f5',
                border: '1px solid #b3b3b3',
              },
              textTransform: 'none',
              fontWeight: 500,
              height: '42px',
              position: 'relative',
              paddingLeft: '48px' // Make room for the logo
            }}
            onClick={handleMicrosoftLogin}
            disabled={microsoftLoading}
          >
            {microsoftLoading ? (
              <CircularProgress size={20} sx={{ position: 'absolute', left: '14px' }} />
            ) : (
              <Box 
                component="img" 
                src="/microsoft-logo.svg" 
                alt="Microsoft Logo"
                sx={{ 
                  position: 'absolute', 
                  left: '14px',
                  width: '20px', 
                  height: '20px'
                }}
              />
            )}
            {language === 'nl' ? 'Inloggen met Microsoft' : 'Sign in with Microsoft'}
          </Button>
          
          {/* Add signup link section */}
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {language === 'nl' ? 'Nog geen account?' : "Don't have an account?"}
            </Typography>
            <Link
              component="button"
              variant="body2"
              onClick={(e) => {
                e.preventDefault();
                if (onSwitchToSignup) {
                  onSwitchToSignup();
                }
              }}
              sx={{ mt: 1, textDecoration: 'underline', cursor: 'pointer' }}
            >
              {language === 'nl' ? 'Account Aanmaken' : 'Create Account'}
            </Link>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default Login; 